import { ENDPOINTS } from '../config/api';
import { apiRequest, assertOk } from './httpClient';

const EXTENSION_MAP = {
  html: { ext: 'html', mime: 'text/html' },
  markdown: { ext: 'md', mime: 'text/markdown' },
  plaintext: { ext: 'txt', mime: 'text/plain' },
  richtext: {
    ext: 'docx',
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
  docx: {
    ext: 'docx',
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
};

/**
 * Call the backend export endpoint and return the exported content.
 * @param {Object} requestData - Export request payload
 * @returns {Promise<{blob: Blob, mimeType: string}>} Exported file payload
 */
export async function exportDocument(requestData) {
  const response = await apiRequest(ENDPOINTS.EXPORT, {
    method: 'POST',
    body: JSON.stringify(requestData),
  });

  await assertOk(response);

  const blob = await response.blob();
  const mimeType = response.headers.get('content-type') || 'application/octet-stream';
  return { blob, mimeType };
}

/**
 * Trigger a browser file download from content string.
 * @param {{blob: Blob, mimeType: string}} payload - Exported payload
 * @param {string} format - Export format key (html, markdown, plaintext, richtext)
 * @param {string} filename - Base filename without extension
 */
export function downloadExport(payload, format, filename = 'ai-summary') {
  const fileInfo = EXTENSION_MAP[format] || EXTENSION_MAP.html;
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const fullFilename = `${filename}_${timestamp}.${fileInfo.ext}`;

  const blob = payload.blob || new Blob([], { type: payload.mimeType || fileInfo.mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fullFilename;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Build the request payload from export data and options.
 * @param {Object} exportData - Content and metadata fields
 * @param {Object} options - Format and include toggles
 * @returns {Object} Request payload for the export endpoint
 */
export function buildExportRequest(exportData, options) {
  return {
    summary: exportData.summary || '',
    format: options.selectedFormat,
    include_metadata: options.includeMetadata && exportData.metadata != null,
    include_glossary: options.includeGlossary && !!exportData.glossary,
    include_quote: options.includeQuote && !!exportData.quote,
    include_citation: options.includeCitation && !!exportData.citation,
    metadata: exportData.metadata ?? null,
    glossary: exportData.glossary ?? '',
    quote: exportData.quote ?? '',
    citation: exportData.citation ?? '',
    original_text: exportData.originalText ?? '',
    method: exportData.method ?? null,
    model: exportData.model ?? null,
    temperature: exportData.temperature ?? null,
    top_p: exportData.topP ?? null,
    intended_audience: exportData.intendedAudience ?? null,
    summary_style: exportData.summaryStyle ?? null,
    file_name: exportData.fileName ?? null,
    file_size: exportData.fileSize ?? null,
    file_type: exportData.fileType ?? null,
    title: 'AI Generated Summary',
  };
}
